import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { formatDate } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';
import { NewsPost } from '../types/news';

const Article = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = React.useState<NewsPost | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;

      try {
        const { data, error } = await supabase
          .from('news_posts')
          .select('*')
          .eq('slug', slug)
          .eq('is_published', true)
          .single();

        if (error) throw error;
        setArticle(data);
      } catch (err) {
        console.error('Error fetching article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-[#2A382A] to-white pt-32 pb-16">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p className="mt-4 text-white/70">Loading article...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !article) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-[#2A382A] to-white pt-32 pb-16">
          <div className="container max-w-4xl mx-auto px-4">
            <h1 className="text-2xl text-white">{error || 'Article not found'}</h1>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#2A382A] via-[#2A382A] to-[#1F2B1F] text-white">
        {/* Hero Section */}
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-24 md:py-32">
          <div className="relative z-10 max-w-6xl mx-auto text-center">
            <div className="flex items-center gap-4 justify-center mb-8">
              <span className="px-3 py-1 bg-[#2A382A] text-white rounded-full text-sm border border-white/20">
                Info
              </span>
              <span className="w-1 h-1 rounded-full bg-white/30" />
              <time className="text-sm text-white/70">
                {formatDate(new Date(article.published_date), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>

            <h1 className="font-barrio text-4xl md:text-5xl lg:text-6xl text-white text-left mb-8 max-w-4xl">
              {article.title}
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-4xl mx-auto text-justify">
              {article.excerpt}
            </p>
          </div>

          {/* Featured Image */}
          <div className="mt-8 md:mt-8 w-full max-w-4xl mx-auto relative z-10 mb-[-12rem]">
            <div className="aspect-[16/9] rounded-2xl overflow-hidden">
              <img
                src={article.image_url}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="container max-w-[95%] xl:max-w-[60vw] mx-auto px-4 pt-32 pb-16 bg-white rounded-t-[2rem] text-[#2A382A]">
          <h2 className="sr-only">Article Content</h2>
          <div 
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          >
          </div>

        </article>
      </div>
    </Layout>
  );
};

export default Article;